/**
 * Runtime configuration - the single source of env-varying values.
 *
 * Build-once, config-at-runtime: env-specific values (API URLs, Keycloak,
 * Firebase, Mapbox, Crikket, feature flags) are NEVER baked into the JS bundle.
 * Instead the Cloudflare Worker in front of the static assets serves
 * `/config.js`, which sets `window.__ENV__` from that Worker's own per-env
 * bindings BEFORE the app bundle runs. One built bundle therefore promotes
 * unchanged from dev to prod - only the Worker's bindings differ.
 *
 * Sources, in order of precedence:
 *   1. `window.__ENV__`      - injected by the Worker (deployed web) or bundled
 *                              `config.js` (Capacitor native builds).
 *   2. Vite `import.meta.env` - local `pnpm dev`, which has no Worker. Reading
 *                              the same values from `.env*` keeps local DX
 *                              identical; these are only present in a dev build.
 *
 * Build-IDENTITY (git SHA, build time, app version) is per-BUILD, not per-env,
 * so it legitimately stays baked at build time - see `buildInfo` below.
 */

type RawEnv = Record<string, string | undefined>;

declare global {
  interface Window {
    __ENV__?: RawEnv;
  }
}

// Local-dev fallback: every runtime key mapped from its Vite counterpart. This
// is the one place the runtime key ↔ VITE_ key correspondence is declared. In a
// deployed bundle these read as undefined (CI no longer passes them), so
// `window.__ENV__` is authoritative there; under `pnpm dev` they come from
// `.env*`.
const viteFallback: RawEnv = {
  API_URL: import.meta.env.VITE_API_URL,
  AI_URL: import.meta.env.VITE_AI_URL,
  PRINT_URL: import.meta.env.VITE_PRINT_URL,
  STOREFRONT_URL: import.meta.env.VITE_STOREFRONT_URL,
  MEDIA_URL: import.meta.env.VITE_MEDIA_URL,
  ONBOARDING_URL: import.meta.env.VITE_ONBOARDING_URL,
  UPDATE_URL: import.meta.env.VITE_UPDATE_URL,

  KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL,
  KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,

  FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
  FIREBASE_VAPID_KEY: import.meta.env.VITE_FIREBASE_VAPID_KEY,

  MAPBOX_TOKEN: import.meta.env.VITE_MAPBOX_TOKEN,
  CRIKKET_KEY: import.meta.env.VITE_CRIKKET_KEY,
  CRIKKET_HOST: import.meta.env.VITE_CRIKKET_HOST,

  UMAMI_HOST: import.meta.env.VITE_UMAMI_HOST,
  UMAMI_WEBSITE_ID: import.meta.env.VITE_UMAMI_WEBSITE_ID,

  // Per-HOST deploy target (dev / staging / prod), set by the Worker binding —
  // mirrors the backend's DEPLOY_ENV. NOT derivable from import.meta.env, which
  // is baked once and identical across the dev-deploy and prod-deploy bundles.
  DEPLOY_ENV: import.meta.env.VITE_DEPLOY_ENV,

  FEATURE_CUSTOMER_SERVICE: import.meta.env.VITE_FEATURE_CUSTOMER_SERVICE,
  FEATURE_INBOX: import.meta.env.VITE_FEATURE_INBOX,
  FEATURE_LOYALTY: import.meta.env.VITE_FEATURE_LOYALTY,
  FEATURE_PURGE: import.meta.env.VITE_FEATURE_PURGE,
  FEATURE_TABLE_ORDER: import.meta.env.VITE_FEATURE_TABLE_ORDER,
  FEATURE_REPORTS: import.meta.env.VITE_FEATURE_REPORTS,
  FEATURE_RESCHEDULE: import.meta.env.VITE_FEATURE_RESCHEDULE,
  FEATURE_AI_ASSISTANT: import.meta.env.VITE_FEATURE_AI_ASSISTANT,
  FEATURE_VOICE_INPUT: import.meta.env.VITE_FEATURE_VOICE_INPUT,
  FEATURE_ACTION_CENTER: import.meta.env.VITE_FEATURE_ACTION_CENTER,
  FEATURE_SETTINGS_ONLINE_ORDERS: import.meta.env.VITE_FEATURE_SETTINGS_ONLINE_ORDERS,
  FEATURE_SETTINGS_TABLE_ORDERING: import.meta.env.VITE_FEATURE_SETTINGS_TABLE_ORDERING,
  FEATURE_SETTINGS_PRINTING: import.meta.env.VITE_FEATURE_SETTINGS_PRINTING,
  FEATURE_SETTINGS_CUSTOMERS: import.meta.env.VITE_FEATURE_SETTINGS_CUSTOMERS,
  FEATURE_SETTINGS_NOTIFICATIONS: import.meta.env.VITE_FEATURE_SETTINGS_NOTIFICATIONS,
  FEATURE_SETTINGS_MENU: import.meta.env.VITE_FEATURE_SETTINGS_MENU,
  FEATURE_SETTINGS_GIFT_CARDS: import.meta.env.VITE_FEATURE_SETTINGS_GIFT_CARDS,
  FEATURE_SETTINGS_STAFF: import.meta.env.VITE_FEATURE_SETTINGS_STAFF,
  FEATURE_OWNER_EMAIL_NOTIFICATIONS: import.meta.env.VITE_FEATURE_OWNER_EMAIL_NOTIFICATIONS,
  FEATURE_PROMO_CODES: import.meta.env.VITE_FEATURE_PROMO_CODES,
};

const raw: RawEnv = typeof window !== 'undefined' && window.__ENV__ ? window.__ENV__ : viteFallback;

/** A configured URL/string value, or the given default when unset/empty. */
function str(key: string, fallback = ''): string {
  const v = raw[key];
  return v === undefined || v === '' ? fallback : v;
}

/** A feature flag: on only when explicitly "true"/"1", off otherwise. */
function flag(key: string): boolean {
  const v = raw[key];
  return v === 'true' || v === '1';
}

/**
 * Env-varying runtime config. URLs carry the same localhost defaults the old
 * scattered `import.meta.env.X || 'http://localhost:…'` reads used, so local
 * dev with no `.env` still works.
 */
export const env = {
  apiUrl: str('API_URL', 'http://localhost:3110'),
  aiUrl: str('AI_URL', 'http://localhost:3113'),
  printUrl: str('PRINT_URL'),
  storefrontUrl: str('STOREFRONT_URL'),
  mediaBaseUrl: str('MEDIA_URL'),
  onboardingUrl: str('ONBOARDING_URL'),
  updateUrl: str('UPDATE_URL'),

  keycloakUrl: str('KEYCLOAK_URL', 'http://localhost:3103'),
  keycloakRealm: str('KEYCLOAK_REALM', 'restaurant'),
  keycloakClientId: str('KEYCLOAK_CLIENT_ID', 'dashboard-client'),

  firebase: {
    apiKey: str('FIREBASE_API_KEY'),
    authDomain: str('FIREBASE_AUTH_DOMAIN'),
    projectId: str('FIREBASE_PROJECT_ID'),
    storageBucket: str('FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: str('FIREBASE_MESSAGING_SENDER_ID'),
    appId: str('FIREBASE_APP_ID'),
    vapidKey: str('FIREBASE_VAPID_KEY'),
  },

  mapboxToken: str('MAPBOX_TOKEN'),
  crikketKey: str('CRIKKET_KEY'),
  crikketHost: str('CRIKKET_HOST'),

  // Behaviour analytics (self-hosted Umami). Unset = tracker never loads, so an
  // env that hasn't been provisioned simply ships without analytics.
  umamiHost: str('UMAMI_HOST'),
  umamiWebsiteId: str('UMAMI_WEBSITE_ID'),

  // Deploy target of THIS host. 'prod' only on the production Worker; dev, local
  // and staging are anything else. Used to force LIVE-only payment connects on
  // prod (see ConnectProviderFormPage). Set DEPLOY_ENV=prod in the prod Worker
  // binding (VITE_DEPLOY_ENV=prod under local dev) for this to take effect.
  deployEnv: str('DEPLOY_ENV', 'development'),
} as const;

/** True only on the production host — gates prod-only UX like LIVE-only payment
 *  connects. Mirrors the backend's `resolveDeployEnv() === 'prod'`. */
export const isProdDeploy = env.deployEnv === 'prod';

/** Feature flags - env-varying, resolved at runtime like everything above. */
export const featureFlags = {
  customerService: flag('FEATURE_CUSTOMER_SERVICE'),
  inbox: flag('FEATURE_INBOX'),
  loyalty: flag('FEATURE_LOYALTY'),
  purge: flag('FEATURE_PURGE'),
  tableOrdering: flag('FEATURE_TABLE_ORDER'),
  reports: flag('FEATURE_REPORTS'),
  // Gates the "Rescheduled" arrival outcome - the mark-rescheduled menu action
  // and the reservations status filter chip. Hidden on every env (dev + prod);
  // flip to `flag('FEATURE_RESCHEDULE')` (+ set the env var) to re-enable. The
  // RESCHEDULED enum + backend handling stay so existing rows still resolve.
  // SEATED ("table assigned") and IN_PROGRESS ("serving") arrival outcomes have
  // no flag and are always available.
  reschedule: false,
  // Gates the "Seated" and "In progress" (serving) arrival outcomes — the
  // mark-seated / mark-in-progress menu actions and their status filter chips.
  // Hidden on every env (dev + prod). The SEATED / IN_PROGRESS enums + backend
  // handling stay, so existing rows still resolve; only the way to SET them (and
  // filter by them) is hidden. Flip to `flag('FEATURE_SEATED_IN_PROGRESS')` to
  // make it env-controlled.
  seatedAndInProgress: false,
  aiAssistant: flag('FEATURE_AI_ASSISTANT'),
  voiceInput: flag('FEATURE_VOICE_INPUT'),
  actionCenter: flag('FEATURE_ACTION_CENTER'),
  settingsOnlineOrders: flag('FEATURE_SETTINGS_ONLINE_ORDERS'),
  settingsTableOrdering: flag('FEATURE_SETTINGS_TABLE_ORDERING'),
  settingsPrinting: flag('FEATURE_SETTINGS_PRINTING'),
  settingsCustomers: flag('FEATURE_SETTINGS_CUSTOMERS'),
  settingsNotifications: flag('FEATURE_SETTINGS_NOTIFICATIONS'),
  settingsMenu: flag('FEATURE_SETTINGS_MENU'),
  settingsGiftCards: flag('FEATURE_SETTINGS_GIFT_CARDS'),
  settingsStaff: flag('FEATURE_SETTINGS_STAFF'),
  // Gates the owner/staff EMAIL settings in Settings → Push notifications' mail
  // page: the reservation "Email to owner & staff of new bookings" row and the
  // whole "Email to owner & staff · by order type" (pickup/delivery) block.
  // Set OFF in both dev + prod (config/build.{dev,prod}.json) to hide these
  // everywhere; flip the env value to "true" to bring them back. Off by default
  // like every flag, so an unset env also hides them.
  ownerEmailNotifications: flag('FEATURE_OWNER_EMAIL_NOTIFICATIONS'),
  // Gates discount CODES in the promotion editor: the "Code required" activation
  // choice and everything it unlocks (shared/personal code kind, the code, the
  // redemption cap). Same env var as the storefront's checkout code input, so
  // one setting turns the whole feature on or off end to end — the dashboard can
  // never author a code the storefront has no way to redeem. Off in dev + prod.
  // An existing CODE_REQUIRED promotion still shows its rows, so a promo created
  // while the flag was on stays visible and editable instead of being stranded.
  promoCodes: flag('FEATURE_PROMO_CODES'),
} as const;

/**
 * Build identity - per-BUILD, not per-env, so these stay baked at build time.
 * `import.meta.env.DEV`/`MODE` remain read directly at their call sites (Vite
 * compile-time constants).
 */
export const buildInfo = {
  gitSha: import.meta.env.VITE_GIT_SHA ?? 'dev',
  buildTime: import.meta.env.VITE_BUILD_TIME ?? '',
  appVersion: import.meta.env.VITE_APP_VERSION ?? '',
} as const;
